import { useEffect, useMemo, useRef, useState } from "react";
import { api, jfetch, type GateBundleView, type MissionListItemView, type QueueItemView } from "../api";
import { statusLabel } from "../domain/statusCopy";
import { toneTextClass } from "./queueStateStyling";
import type { OwnerNavId } from "./ownerNavigation";

/**
 * OWNEXP Slice 10 — Global Search / Command Palette (Strg+K).
 *
 * KEINE zweite Such-Authority: die Palette liest ausschließlich die
 * bestehenden Read-Models (Missions-, Queue-, Findings-, Gate-Endpunkte) und
 * filtert lokal. Navigation/Aktionen laufen über die kanonischen Deep-Links
 * bzw. Callbacks — kein zweiter Ausführungspfad. Keyboard-first: Tippen
 * filtert, Pfeiltasten wählen, Enter öffnet, Escape schließt.
 */

interface FindingRow {
  finding_id: string;
  severity: string;
  finding_class: string;
  found_by: string | null;
  status: string;
}

interface Quellen {
  missionen: MissionListItemView[] | null;
  queue: QueueItemView[] | null;
  findings: FindingRow[] | null;
  gates: GateBundleView[] | null;
}

interface Treffer {
  key: string;
  gruppe: "Aktion" | "Mission" | "Queue-Item" | "Finding" | "Human Gate";
  titel: string;
  detail: string;
  ausfuehren: () => void;
}

export function CommandPalette({
  onClose,
  onNavigate,
  onOpenMission,
}: {
  onClose: () => void;
  onNavigate: (id: OwnerNavId) => void;
  onOpenMission: (missionId: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState("");
  const [quellen, setQuellen] = useState<Quellen>({ missionen: null, queue: null, findings: null, gates: null });
  const [auswahl, setAuswahl] = useState(0);
  const listenRef = useRef<HTMLDivElement | null>(null);

  // Bestehende Read-Models einmal beim Öffnen laden (bounded, fail-soft je Quelle).
  useEffect(() => {
    let abgebaut = false;
    void Promise.all([
      api.missions().catch(() => null),
      api.controlPlaneQueue(200).catch(() => null),
      jfetch<{ ok: boolean; findings: FindingRow[] }>("/api/findings?limit=100").catch(() => null),
      api.gateBundles().catch(() => null),
    ]).then(([m, q, f, g]) => {
      if (abgebaut) return;
      setQuellen({
        missionen: m?.missions.slice(0, 80) ?? null,
        queue: q?.items ?? null,
        findings: f?.findings ?? null,
        gates: g?.bundles ?? null,
      });
    });
    return () => {
      abgebaut = true;
    };
  }, []);

  const treffer = useMemo<Treffer[]>(() => {
    const q = query.trim().toLowerCase();
    const passt = (...felder: Array<string | null | undefined>): boolean =>
      q.length === 0 || felder.filter(Boolean).join(" ").toLowerCase().includes(q);
    const liste: Treffer[] = [];

    // ── Feste Aktionen (Owner-Job-Abkürzungen) ──
    const aktionen: Array<[string, string, () => void]> = [
      ["Aktuelle Mission öffnen", "Missions & Runs mit laufenden Runs", () => { onNavigate("control"); window.location.hash = "#/control/missions-runs"; }],
      ["Wartende Items zeigen", "Queue gefiltert auf Wartet-auf-Owner", () => { onNavigate("control"); window.location.hash = "#/control/queue?state=WAIT_OWNER"; }],
      ["Fehlgeschlagene Items zeigen", "Queue gefiltert auf FAILED", () => { onNavigate("control"); window.location.hash = "#/control/queue?state=FAILED"; }],
      ["Human Gates zeigen", "Offene Owner-Entscheidungen", () => { onNavigate("control"); window.location.hash = "#/control/gates"; }],
      ["Evidence & Reports zeigen", "Belege und Abschlussberichte je Mission", () => { onNavigate("control"); window.location.hash = "#/control/evidence"; }],
    ];
    for (const [titel, detail, ausfuehren] of aktionen) {
      if (passt(titel, detail)) liste.push({ key: `akt-${titel}`, gruppe: "Aktion", titel, detail, ausfuehren });
    }

    // ── Missionen ──
    for (const m of quellen.missionen ?? []) {
      if (passt(m.goal, m.id, m.status)) {
        liste.push({
          key: `m-${m.id}`,
          gruppe: "Mission",
          titel: m.goal.slice(0, 90),
          detail: `${statusLabel(m.status) ?? m.status} · ${m.id}`,
          ausfuehren: () => onOpenMission(m.id),
        });
      }
    }

    // ── Queue-Items ──
    for (const it of quellen.queue ?? []) {
      if (passt(it.title, it.id, it.goal ?? "", it.missionId ?? "")) {
        liste.push({
          key: `q-${it.id}`,
          gruppe: "Queue-Item",
          titel: it.title,
          detail: `${statusLabel(it.queueState) ?? it.queueState ?? "unverwaltet"} · ${it.priority} · ${it.id}`,
          ausfuehren: () => { onNavigate("control"); window.location.hash = `#/control/queue?q=${encodeURIComponent(it.id)}`; },
        });
      }
    }

    // ── Findings ──
    for (const f of quellen.findings ?? []) {
      if (passt(f.finding_class, f.finding_id, f.severity, f.found_by)) {
        liste.push({
          key: `f-${f.finding_id}`,
          gruppe: "Finding",
          titel: `${f.severity} · ${f.finding_class}`,
          detail: `${f.status} · gefunden von ${f.found_by ?? "—"} · ${f.finding_id}`,
          ausfuehren: () => { onNavigate("control"); window.location.hash = "#/control/findings"; },
        });
      }
    }

    // ── Human Gates (frische Bundles) ──
    for (const b of quellen.gates ?? []) {
      for (const g of b.gates) {
        if (passt(g.whyRequired, g.exactAction, g.actorClass, g.gateId)) {
          liste.push({
            key: `g-${g.gateId}`,
            gruppe: "Human Gate",
            titel: g.exactAction,
            detail: `${g.actorClass} · ${g.whyRequired}`,
            ausfuehren: () => { onNavigate("control"); window.location.hash = "#/control/gates"; },
          });
        }
      }
    }

    return liste.slice(0, 40);
  }, [query, quellen, onNavigate, onOpenMission]);

  useEffect(() => {
    setAuswahl(0);
  }, [query]);

  const waehlen = (treffer: Treffer): void => {
    treffer.ausfuehren();
    onClose();
  };

  const nichtVerfuegbar = [
    quellen.missionen === null ? "Missionen" : null,
    quellen.queue === null ? "Queue" : null,
    quellen.findings === null ? "Findings" : null,
    quellen.gates === null ? "Gates" : null,
  ].filter(Boolean) as string[];

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start bg-black/55 px-4 pt-[12vh] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Suche und Befehle"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mx-auto w-full max-w-2xl rounded-2xl border border-jarvis-border bg-jarvis-surface p-4 shadow-[0_30px_80px_rgba(0,0,0,0.48)]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-jarvis-accent">Strg+K</p>
            <h2 className="text-lg font-semibold">Suche &amp; Befehle</h2>
          </div>
          <button className="topbar-control" type="button" onClick={onClose}>Schließen</button>
        </div>
        <input
          className="input mt-3 min-h-11 w-full text-sm"
          placeholder="Suchen: Mission, Queue-Item, Finding, Human Gate … oder Aktion"
          value={query}
          autoFocus
          aria-label="Suche und Befehle"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setAuswahl((i) => Math.min(i + 1, treffer.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setAuswahl((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              const t = treffer[auswahl];
              if (t) waehlen(t);
            }
          }}
          data-testid="command-palette-input"
        />
        {nichtVerfuegbar.length > 0 && (
          <p className={`mt-2 text-[11px] ${toneTextClass("warning")}`} data-testid="command-palette-degraded">
            Quelle(n) nicht verfügbar: {nichtVerfuegbar.join(", ")} — die restliche Suche funktioniert weiter.
          </p>
        )}
        <div className="simo-thin-scroll mt-3 max-h-[46vh] overflow-y-auto" ref={listenRef} role="listbox" aria-label="Treffer">
          {treffer.length === 0 ? (
            <p className="p-3 text-sm text-jarvis-muted" data-testid="command-palette-empty">
              Kein Treffer — Suche präzisieren oder Aktion über den Namen finden.
            </p>
          ) : (
            treffer.map((t, i) => (
              <button
                key={t.key}
                role="option"
                aria-selected={i === auswahl}
                className={`flex w-full items-baseline justify-between gap-3 rounded-xl border p-3 text-left ${
                  i === auswahl ? "border-jarvis-accent/50 bg-jarvis-accent/10" : "border-white/10 bg-black/20 hover:border-jarvis-accent/30"
                }`}
                onMouseEnter={() => setAuswahl(i)}
                onClick={() => waehlen(t)}
                data-testid={`command-palette-item-${i}`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-jarvis-text">{t.titel}</span>
                  <span className="mt-0.5 block truncate text-xs text-jarvis-muted">{t.detail}</span>
                </span>
                <span className="shrink-0 rounded-md border border-white/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-jarvis-muted">
                  {t.gruppe}
                </span>
              </button>
            ))
          )}
        </div>
        <p className="mt-2 text-[10px] text-jarvis-muted">Pfeiltasten wählen · Enter öffnet · Escape schließt — Quellen: Missions-/Queue-/Findings-/Gate-Read-Models (keine eigene Such-Authority).</p>
      </div>
    </div>
  );
}
